/* ============================================================
 * EOI / DEAL DOCUMENT THUMBNAILS — ONE TAP TARGET, ONE VIEWER (2026-10-01)
 * ------------------------------------------------------------
 * Owner request: on mobile, tapping ANYWHERE on a document image opens the
 * viewer; the whole thumbnail is the tap target (44px minimum); the ✕ delete
 * stays its own target and never opens the viewer.
 *
 * WHAT WAS WRONG. Each of the four thumbnail sites (EOI image, EOI documents,
 * Deal contract images, Deal documents) put the onClick on the <img> itself and
 * opened the "viewer" with `var w = window.open(); w.document.write(...)`.
 * Inside the Capacitor WebViews window.open() returns null, so the tap threw
 * and nothing opened — the same shape as printPdf.js's window.print() story.
 * The ✕ sat inside the same 80px box as an 18px button with no stopPropagation
 * to rely on except being a sibling rather than a child.
 *
 * WHAT THIS IS. TxnDocThumb renders the thumbnail: the WRAPPER carries the tap
 * handler (the image has pointer-events:none so there is exactly one target),
 * the box is never smaller than 44px, and the ✕ — rendered only when onDelete
 * is given — is a 40px hit box whose click stops propagation before calling
 * onDelete, so it can never reach onOpen. DocViewer is the in-app full-screen
 * viewer the tap opens: a fixed overlay with the image fitted to the screen,
 * closed by tapping the backdrop, the ✕, or Escape; pinch / double-tap zoom and
 * pan while zoomed (2026-10-02), reset on every close. Works identically on the
 * website and inside the apps because it is DOM, not a popup.
 *
 * verify-doc-thumb-tap.js (root build chain, TIER2) renders both and invokes
 * the handlers; it is the executable form of the three sentences above.
 * ============================================================ */
import { useEffect, useRef, useState } from "react";

var MIN_TAP = 44;
var RED = "rgba(220,38,38,0.92)";

export function TxnDocThumb(props) {
  var size = Math.max(MIN_TAP, Number(props.size) || 80);
  var onOpen = typeof props.onOpen === "function" ? props.onOpen : null;
  var onDelete = typeof props.onDelete === "function" ? props.onDelete : null;
  var wrap = {
    position: "relative", width: size, height: size, minWidth: MIN_TAP, minHeight: MIN_TAP,
    flex: "0 0 auto", borderRadius: 8, overflow: "hidden", background: "#fff",
    border: props.bordered === false ? "none" : "1px solid #E2E8F0",
    cursor: onOpen ? "zoom-in" : "default", WebkitTapHighlightColor: "transparent"
  };
  return (
    <div role={onOpen ? "button" : undefined} tabIndex={onOpen ? 0 : undefined}
         aria-label={onOpen ? ("View " + (props.alt || "document")) : undefined}
         title={props.title || props.alt || ""} style={wrap}
         data-doc-thumb="1"
         onClick={onOpen ? function (e) { if (e && e.preventDefault) e.preventDefault(); onOpen(); } : undefined}
         onKeyDown={onOpen ? function (e) { if (e && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } } : undefined}>
      <img decoding="async" src={props.src} alt={props.alt || ""} draggable={false}
           style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none", userSelect: "none" }}/>
      {onDelete && (
        <button type="button" title={props.deleteTitle || "Remove"} aria-label={props.deleteTitle || "Remove"}
                data-doc-delete="1"
                onClick={function (e) { if (e && e.stopPropagation) e.stopPropagation(); if (e && e.preventDefault) e.preventDefault(); onDelete(); }}
                onMouseDown={function (e) { if (e && e.stopPropagation) e.stopPropagation(); }}
                onTouchStart={function (e) { if (e && e.stopPropagation) e.stopPropagation(); }}
                style={{ position: "absolute", top: 0, right: 0, width: 40, height: 40, minWidth: 40, minHeight: 40, padding: 0,
                         background: "transparent", border: "none", cursor: "pointer",
                         display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
          <span aria-hidden="true" style={{ margin: 3, width: 22, height: 22, borderRadius: "50%", background: RED, color: "#fff",
                                             fontSize: 13, fontWeight: 700, lineHeight: "22px", textAlign: "center", display: "block",
                                             boxShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>×</span>
        </button>
      )}
    </div>
  );
}

/* ---------- ZOOM MATH — pure, exported, gate-run (2026-10-02) -------------
 * The viewer's gestures reduce to three pure transitions over
 * { scale, x, y } (x/y = translation in CSS px, applied before the scale):
 *   zoomPinch(prev, startDist, dist, startScale)  — two fingers: scale follows
 *        the distance ratio from the scale the pinch started at, clamped;
 *   zoomDoubleTap(prev)                            — 1 ⇄ ZOOM_DBL at the tap;
 *   zoomPan(prev, dx, dy, bounds)                  — one finger while zoomed:
 *        translate, clamped so the image edge never leaves the viewport edge.
 * ZOOM_RESET is what every open and every close start from.
 * verify-doc-thumb-tap.js runs these over fixtures rather than the gestures. */
export var ZOOM_MIN = 1, ZOOM_MAX = 4, ZOOM_DBL = 2.5;
export var ZOOM_RESET = { scale: 1, x: 0, y: 0 };
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
export function zoomPinch(prev, startDist, dist, startScale) {
  if (!(startDist > 0) || !(dist > 0)) return prev || ZOOM_RESET;
  var scale = clamp((startScale || (prev && prev.scale) || 1) * (dist / startDist), ZOOM_MIN, ZOOM_MAX);
  var next = { scale: scale, x: prev ? prev.x : 0, y: prev ? prev.y : 0 };
  return scale === ZOOM_MIN ? ZOOM_RESET : next;
}
export function zoomDoubleTap(prev) {
  return (prev && prev.scale > ZOOM_MIN) ? ZOOM_RESET : { scale: ZOOM_DBL, x: 0, y: 0 };
}
export function zoomPan(prev, dx, dy, bounds) {
  var p = prev || ZOOM_RESET;
  if (p.scale <= ZOOM_MIN) return p;                       // nothing to pan at 1:1
  // Max travel = how far the scaled image overhangs the viewport on each side.
  var maxX = bounds ? Math.max(0, (bounds.width  * p.scale - bounds.width)  / 2) : Infinity;
  var maxY = bounds ? Math.max(0, (bounds.height * p.scale - bounds.height) / 2) : Infinity;
  return { scale: p.scale, x: clamp(p.x + (dx || 0), -maxX, maxX), y: clamp(p.y + (dy || 0), -maxY, maxY) };
}
export function zoomTransform(z) {
  var s = z || ZOOM_RESET;
  return "translate(" + (s.x || 0) + "px, " + (s.y || 0) + "px) scale(" + (s.scale || 1) + ")";
}

var DBL_TAP_MS = 300;
function touchDist(t) { var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY; return Math.sqrt(dx * dx + dy * dy); }

/* The in-app viewer. Pinch to zoom, double-tap to zoom, pan while zoomed,
 * reset on every close/open; the backdrop tap and ✕ close; Escape closes.
 * Touch handling is on the overlay with touch-action:none so neither the
 * Capacitor WebView nor a mobile browser zooms the PAGE instead of the image.
 * Gesture state (start distance, last finger position, last tap time) lives in
 * refs; only the resulting { scale, x, y } is React state. */
export function DocViewer(props) {
  var src = props.src || "";
  var onClose = typeof props.onClose === "function" ? props.onClose : function () {};
  // The Escape listener reads the LATEST onClose through a ref, so the effect
  // depends on `src` alone (callers pass inline closures that change every render).
  var onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  var [zoom, setZoom] = useState(ZOOM_RESET);   // destructured so the linter sees a stable setter
  var g = useRef({ pinchStart: 0, pinchScale: 1, lastX: 0, lastY: 0, lastTap: 0, moved: false, bounds: null });
  var frameRef = useRef(null);
  useEffect(function () {
    setZoom(ZOOM_RESET);                                   // every open AND every close starts at 1:1
    if (!src) return undefined;
    var onKey = function (e) { if (e && e.key === "Escape") onCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [src]);
  if (!src) return null;
  var close = function () { setZoom(ZOOM_RESET); onClose(); };
  var bounds = function () { var el = frameRef.current; return el ? { width: el.clientWidth, height: el.clientHeight } : null; };
  var onTouchStart = function (e) {
    var t = e.touches; g.current.moved = false;
    if (t.length === 2) { g.current.pinchStart = touchDist(t); g.current.pinchScale = zoom.scale; }
    else if (t.length === 1) { g.current.lastX = t[0].clientX; g.current.lastY = t[0].clientY; }
  };
  var onTouchMove = function (e) {
    var t = e.touches;
    if (t.length === 2) { if (e.cancelable) e.preventDefault(); g.current.moved = true; setZoom(function (z) { return zoomPinch(z, g.current.pinchStart, touchDist(t), g.current.pinchScale); }); }
    else if (t.length === 1 && zoom.scale > ZOOM_MIN) {
      if (e.cancelable) e.preventDefault();
      var dx = t[0].clientX - g.current.lastX, dy = t[0].clientY - g.current.lastY;
      g.current.lastX = t[0].clientX; g.current.lastY = t[0].clientY;
      if (Math.abs(dx) + Math.abs(dy) > 2) g.current.moved = true;
      var b = bounds();
      setZoom(function (z) { return zoomPan(z, dx, dy, b); });
    }
  };
  var onTouchEnd = function (e) {
    if (e.touches && e.touches.length) return;             // a finger is still down (pinch → pan hand-off)
    if (g.current.moved) return;                           // a drag/pinch is not a tap
    var now = Date.now();
    if (now - g.current.lastTap < DBL_TAP_MS) { g.current.lastTap = 0; setZoom(function (z) { return zoomDoubleTap(z); }); if (e.cancelable) e.preventDefault(); }
    else g.current.lastTap = now;
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={props.alt || "Document"} data-doc-viewer="1" ref={frameRef}
         onClick={function () { if (zoom.scale > ZOOM_MIN) return; close(); }}
         onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
         onDoubleClick={function (e) { if (e && e.preventDefault) e.preventDefault(); setZoom(function (z) { return zoomDoubleTap(z); }); }}
         style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,0.92)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 12, boxSizing: "border-box",
                  overflow: "hidden", touchAction: "none", overscrollBehavior: "contain", WebkitTapHighlightColor: "transparent",
                  cursor: zoom.scale > ZOOM_MIN ? "grab" : "default" }}>
      <img src={src} alt={props.alt || ""} draggable={false} data-doc-viewer-img="1"
           onClick={function (e) { if (e && e.stopPropagation) e.stopPropagation(); }}
           style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6, boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                    transform: zoomTransform(zoom), transformOrigin: "center center", transition: g.current.moved ? "none" : "transform 120ms ease-out",
                    willChange: "transform", userSelect: "none", WebkitUserSelect: "none", pointerEvents: "auto" }}/>
      <button type="button" aria-label="Close" title="Close" data-doc-viewer-close="1"
              onClick={function (e) { if (e && e.stopPropagation) e.stopPropagation(); close(); }}
              onTouchEnd={function (e) { if (e && e.stopPropagation) e.stopPropagation(); if (e && e.preventDefault) e.preventDefault(); close(); }}
              style={{ position: "absolute", top: "max(10px, env(safe-area-inset-top))", right: 10, width: MIN_TAP, height: MIN_TAP,
                       borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff",
                       fontSize: 22, fontWeight: 700, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
      {zoom.scale > ZOOM_MIN && <div aria-hidden="true" data-doc-viewer-zoom="1"
           style={{ position: "absolute", bottom: "max(12px, env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)",
                    padding: "4px 10px", borderRadius: 12, background: "rgba(255,255,255,0.14)", color: "#fff", fontSize: 12, fontWeight: 600 }}>
        {Math.round(zoom.scale * 100) + "% · double-tap to reset"}</div>}
    </div>
  );
}
